import type { Placement } from '@floating-ui/react';
import React from 'react';

import { webConfig } from '@gitbook/env';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    SidebarButton,
    type SidebarButtonProps,
} from '@gitbook/spine-react';

import { useUnreadNotificationsCount } from './notifications';
import { NotificationsPanel } from './NotificationsPanel';

type NotificationsBellProps = SidebarButtonProps & {
    unreadCount?: number;
    showCount?: boolean;
    placement?: Placement;
};

/**
 * Button to open the notification panel.
 */
export function NotificationsBell(props: NotificationsBellProps): React.ReactElement {
    if (process.env.GITBOOK_PREDICTABLE_UI) {
        return <PredictableNotificationsBell {...props} />;
    }

    if (!webConfig.magicbell.apiKey) {
        return <NoopNotificationsBell {...props} />;
    }

    return <MagicBellNotificationsBell {...props} />;
}

/**
 * An implementation of the notification bell used for predictable UIs.
 */
function PredictableNotificationsBell(props: NotificationsButtonProps): React.ReactElement {
    return <NotificationsButton {...props} opened={false} unreadCount={3} />;
}

/**
 * An implementation of the notification bell that does nothing.
 */
function NoopNotificationsBell(props: NotificationsButtonProps): React.ReactElement {
    return <NotificationsButton {...props} opened={false} />;
}

/**
 * An implementation of the notification bell, backed by MagicBell.
 */
function MagicBellNotificationsBell(props: NotificationsBellProps): React.ReactElement {
    const unreadCount = useUnreadNotificationsCount();

    return (
        <PopoverRoot>
            <PopoverTrigger>
                <NotificationsButton unreadCount={unreadCount} {...props} />
            </PopoverTrigger>
            <Popover placement={props.placement ?? 'right-start'}>
                <NotificationsPanel />
            </Popover>
        </PopoverRoot>
    );
}

type NotificationsButtonProps = NotificationsBellProps & { opened?: boolean };

const NotificationsButton = function NotificationsButton({
    ref,
    ...props
}: NotificationsButtonProps & { ref?: React.ForwardedRef<HTMLButtonElement> }): React.ReactElement {
    const { unreadCount, opened, onPress, icon, label, showCount = false, ...rest } = props;
    const hasUnread = unreadCount && unreadCount > 0;

    return (
        <SidebarButton
            ref={ref}
            data-testid="sidebar.notificationsButton"
            icon={icon ?? (hasUnread ? Icon.BellUnread : Icon.Bell)}
            label={label ?? 'Notifications'}
            aria-expanded={opened}
            onPress={onPress}
            {...rest}
        >
            {showCount && (
                <Badge
                    label={unreadCount}
                    kind="secondary"
                    size="xsmall"
                    className="ml-auto"
                    thin
                />
            )}
        </SidebarButton>
    );
};
